import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, PlusCircle, RefreshCcw, Search, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../lib/axios";
import {
  AdminPageHero,
  AdminDataTableShell,
  adminHeroActionClassName,
} from "../../modules/admin/ui";
import AffecterRessourceModal from "../../components/ressources/AffecterRessourceModal";
import ResourceStockEntryModal from "../resources/ResourceStockEntryModal";
import type {
  AffectationRow,
  InventoryKpis,
  RessourceOperationnelleRow,
} from "../../types/ressourcesOperationnelles";

type TabKey =
  | "inventaire"
  | "stockCatalogue"
  | "mouvements"
  | "mouvementsCatalogue"
  | "affectations"
  | "maintenance"
  | "alertes";

type CatalogDashboardRow = {
  resourceCatalogId: number;
  code: string;
  displayName: string;
  nom: string;
  category: string;
  family: string | null;
  nature: string;
  famille: string | null;
  unite: string;
  seuilAlerte: number | null;
  actif: boolean;
  stockActuel: number;
  reserve: number;
  disponible: number;
  maintenance: number;
  perte: number;
  consommeMois: number;
  alertes: string[];
  dernierMouvementAt: string | null;
  dernierMouvementKind: string | null;
};

type CatalogFamilySummary = {
  famille: string;
  stockActuel: number;
  reserve: number;
  disponible: number;
  seuilAlerte: number;
  stockFaible: number;
  ruptures: number;
  sortiesMois: number;
};

type CatalogInventorySummary = {
  rows: CatalogDashboardRow[];
  families: CatalogFamilySummary[];
  stats: {
    stockTotal: number;
    stockDisponible: number;
    stockAffecte: number;
    stockFaible: number;
    rupturesCritiques: number;
    sortiesMois: number;
    dotationsActives: number;
  };
};

type CatalogMovementRow = {
  id: number;
  kind: string;
  quantity: number;
  createdAt: string;
  fournisseur: string | null;
  commentaire: string | null;
  resourceCatalog: { code: string; nom: string };
};

const TAB_LABELS: { key: TabKey; label: string }[] = [
  { key: "stockCatalogue", label: "Stock catalogue" },
  { key: "mouvementsCatalogue", label: "Mouvements (catalogue)" },
  { key: "affectations", label: "Affectations" },
  { key: "maintenance", label: "Maintenance" },
  { key: "alertes", label: "Alertes" },
  { key: "inventaire", label: "Ancien inventaire" },
  { key: "mouvements", label: "Logs legacy" },
];

function apiErr(err: unknown, fallback: string): string {
  const ax = err as { response?: { data?: { error?: { message?: string } | string } } };
  const e = ax.response?.data?.error;
  if (typeof e === "string") return e;
  if (e && typeof e === "object" && "message" in e) return String(e.message);
  return fallback;
}

const RessourcesOperationnellesPage: React.FC = () => {
  const [rows, setRows] = useState<RessourceOperationnelleRow[]>([]);
  const [kpis, setKpis] = useState<InventoryKpis | null>(null);
  const [affectations, setAffectations] = useState<AffectationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabKey>("stockCatalogue");
  const [stockEntryOpen, setStockEntryOpen] = useState(false);
  const [stockEntryCatalogId, setStockEntryCatalogId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [affectOpen, setAffectOpen] = useState(false);
  const [selected, setSelected] = useState<RessourceOperationnelleRow | null>(null);
  const [movements, setMovements] = useState<Array<Record<string, unknown>>>([]);
  const [catalogDashboard, setCatalogDashboard] = useState<CatalogDashboardRow[]>([]);
  const [catalogSummary, setCatalogSummary] = useState<CatalogInventorySummary | null>(null);
  const [catalogMovements, setCatalogMovements] = useState<CatalogMovementRow[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, movementRes, kpiRes, affRes, summaryRes] = await Promise.all([
        api.get("/ressources-operationnelles"),
        api.get("/ressources-operationnelles/mouvements").catch(() => ({ data: { data: [] } })),
        api.get("/ressources-operationnelles/kpis").catch(() => ({ data: { data: null } })),
        api.get("/ressources-operationnelles/affectations").catch(() => ({ data: { data: [] } })),
        api.get("/resources/stock/summary").catch(() => ({ data: { data: null } })),
      ]);
      const list = listRes.data?.data ?? [];
      setRows(list as RessourceOperationnelleRow[]);
      const mv = movementRes.data?.data ?? [];
      setMovements(Array.isArray(mv) ? (mv as Array<Record<string, unknown>>) : []);
      setKpis((kpiRes.data?.data ?? null) as InventoryKpis | null);
      setAffectations((affRes.data?.data ?? []) as AffectationRow[]);
      const summary = (summaryRes.data?.data ?? null) as CatalogInventorySummary | null;
      setCatalogSummary(summary);
      setCatalogDashboard(summary?.rows ?? []);
    } catch (err) {
      setRows([]);
      setMovements([]);
      setKpis(null);
      setAffectations([]);
      setCatalogSummary(null);
      setCatalogDashboard([]);
      toast.error(apiErr(err, "Impossible de charger les ressources."), {
        toastId: "ressources-page-load",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const loadCatalogTabs = useCallback(async () => {
    if (tab === "stockCatalogue") {
      try {
        const res = await api.get("/resources/stock/summary");
        const summary = (res.data?.data ?? null) as CatalogInventorySummary | null;
        setCatalogSummary(summary);
        setCatalogDashboard(summary?.rows ?? []);
      } catch {
        setCatalogDashboard([]);
      }
    }
    if (tab === "mouvementsCatalogue") {
      try {
        const res = await api.get("/resources/movements", { params: { limit: 250 } });
        setCatalogMovements((res.data?.rows ?? []) as CatalogMovementRow[]);
      } catch {
        setCatalogMovements([]);
      }
    }
  }, [tab]);

  useEffect(() => {
    if (tab === "stockCatalogue" || tab === "mouvementsCatalogue") {
      void loadCatalogTabs();
    }
  }, [tab, loadCatalogTabs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const matchesSearch =
        !q ||
        [r.reference, r.nom, r.famille, r.etat, r.disponibilite]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(q));
      if (!matchesSearch) return false;
      switch (tab) {
        case "inventaire":
          return true;
        case "maintenance":
          return r.disponibilite === "MAINTENANCE";
        case "affectations":
          return r.stockAffecte > 0;
        case "alertes":
          return r.alertes.length > 0;
        case "mouvements":
        case "mouvementsCatalogue":
          return true;
        case "stockCatalogue":
          return true;
        default:
          return true;
      }
    });
  }, [rows, tab, search]);

  const filteredCatalogDashboard = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return catalogDashboard;
    return catalogDashboard.filter((row) =>
      [row.code, row.displayName, row.nom, row.category, row.family, row.famille, row.nature]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [catalogDashboard, search]);

  const filteredCatalogMovements = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return catalogMovements;
    return catalogMovements.filter((row) =>
      [row.kind, row.fournisseur, row.commentaire, row.resourceCatalog?.code, row.resourceCatalog?.nom]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [catalogMovements, search]);

  const doStockAction = async (
    r: RessourceOperationnelleRow,
    action: "ENTREE" | "SORTIE" | "RETOUR" | "AJUSTEMENT"
  ) => {
    const raw = window.prompt(`Quantité pour ${action} (${r.nom})`, "1");
    if (!raw) return;
    const quantite = Number(raw);
    if (!Number.isInteger(quantite) || quantite < 1) {
      toast.error("Quantité invalide.");
      return;
    }
    try {
      await api.post(`/ressources-operationnelles/${r.id}/stock-actions`, { action, quantite });
      toast.success(`Mouvement ${action} enregistré.`);
      await load();
    } catch (err) {
      toast.error(apiErr(err, "Action stock impossible."));
    }
  };

  const doCatalogStockAction = async (
    row: CatalogDashboardRow,
    action: "SORTIE" | "AJUSTEMENT"
  ) => {
    const raw = window.prompt(`Quantité pour ${action} (${row.nom})`, action === "AJUSTEMENT" ? "1" : "1");
    if (!raw) return;
    const quantity = Number(raw);
    if (!Number.isInteger(quantity) || quantity === 0 || (action === "SORTIE" && quantity < 1)) {
      toast.error("Quantité invalide.");
      return;
    }
    try {
      if (action === "SORTIE") {
        await api.post("/resources/stock/exits", {
          resourceCatalogId: row.resourceCatalogId,
          quantity,
          commentaire: "Sortie manuelle depuis dashboard ressources",
        });
      } else {
        await api.post("/resources/stock/adjustments", {
          resourceCatalogId: row.resourceCatalogId,
          quantity,
          commentaire: "Ajustement manuel depuis dashboard ressources",
        });
      }
      toast.success("Mouvement catalogue enregistré.");
      await load();
      await loadCatalogTabs();
    } catch (err) {
      toast.error(apiErr(err, "Mouvement catalogue impossible."));
    }
  };

  const handleRetirer = async (r: RessourceOperationnelleRow) => {
    if (
      !window.confirm(
        `Retirer « ${r.nom} » du parc actif ? (pas de suppression physique)`
      )
    ) {
      return;
    }
    try {
      await api.delete(`/ressources-operationnelles/${r.id}`);
      toast.success("Ressource retirée du parc actif.");
      void load();
    } catch (err) {
      toast.error(apiErr(err, "Impossible de retirer la ressource."), {
        toastId: `res-del-${r.id}`,
      });
    }
  };

  const familyCards = useMemo(() => {
    const source = catalogSummary?.families ?? [];
    const fallbackFamilies = ["BAC", "POUBELLE", "SAC", "CONTENEUR", "EPI", "OUTIL"];
    return fallbackFamilies.map((famille) => {
      const x = source.find((row) => row.famille === famille);
      const stockActuel = x?.stockActuel ?? 0;
      const reserve = x?.reserve ?? 0;
      const disponible = x?.disponible ?? 0;
      const seuil = x?.seuilAlerte ?? 0;
      const statut = disponible === 0 ? "Rupture" : x && x.stockFaible > 0 ? "Stock faible" : "OK";
      return { famille, stockActuel, stockAffecte: reserve, stockDisponible: disponible, seuil, statut };
    });
  }, [catalogSummary?.families]);

  return (
    <div className="space-y-5 text-slate-100">
      <AdminPageHero
        eyebrow="Terrain"
        title="Ressources opérationnelles"
        subtitle="Source de vérité : catalogue produits + stock agrégé + mouvements stock."
        actions={
          <>
            <button
              type="button"
              onClick={() => {
                setStockEntryCatalogId(null);
                setStockEntryOpen(true);
              }}
              className={adminHeroActionClassName(false)}
            >
              <PlusCircle className="h-4 w-4" />
              Entrée stock (catalogue)
            </button>
            <Link
              to="/admin/ressources/catalogue"
              className={adminHeroActionClassName(false)}
            >
              Catalogue produits
            </Link>
            <Link
              to="/admin/ressources/mouvements"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
            >
              Mouvements catalogue
            </Link>
            <button
              type="button"
              onClick={() => void load()}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
            >
              <RefreshCcw className="h-4 w-4" />
              Actualiser
            </button>
          </>
        }
      />

      <section className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
        {(
          [
            ["Stock total", catalogSummary?.stats.stockTotal ?? kpis?.stockTotal ?? 0, "text-emerald-300"],
            ["Stock disponible", catalogSummary?.stats.stockDisponible ?? kpis?.stockDisponible ?? 0, "text-emerald-300"],
            ["Stock affecté", catalogSummary?.stats.stockAffecte ?? kpis?.materielsAffectes ?? 0, "text-sky-300"],
            ["Stock faible", catalogSummary?.stats.stockFaible ?? kpis?.stockFaible ?? 0, "text-orange-300"],
            ["Maintenance", kpis?.maintenance ?? 0, "text-violet-300"],
            ["Rupture critique", catalogSummary?.stats.rupturesCritiques ?? kpis?.ruptureCritique ?? 0, "text-rose-300"],
            ["Sorties du mois", catalogSummary?.stats.sortiesMois ?? kpis?.sortiesMois ?? 0, "text-amber-300"],
            ["Dotations actives", catalogSummary?.stats.dotationsActives ?? kpis?.dotationsClientsActives ?? 0, "text-sky-300"],
          ] as const
        ).map(([label, val, color]) => (
          <div
            key={String(label)}
            className="rounded-2xl border border-slate-800 bg-slate-900/50 px-4 py-3"
          >
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              {label}
            </p>
            <p className={`mt-1 text-2xl font-bold tabular-nums ${color}`}>{val}</p>
          </div>
        ))}
      </section>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-300">
          Stock réel par famille catalogue
        </h3>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
          {familyCards.map((c) => (
            <div key={c.famille} className="rounded-2xl border border-slate-800 bg-slate-950/40 p-3">
              <p className="text-xs font-semibold text-slate-300">{c.famille === "SAC" ? "Sacs" : c.famille}</p>
              <p className="mt-1 text-xs text-slate-500">Stock: {c.stockActuel}</p>
              <p className="text-xs text-slate-500">Affecte: {c.stockAffecte}</p>
              <p className="text-xs text-slate-500">Disponible: {c.stockDisponible}</p>
              <p className="text-xs text-slate-500">Seuil: {c.seuil}</p>
              <p className={`mt-1 text-xs font-semibold ${c.statut === "OK" ? "text-emerald-300" : c.statut === "Stock faible" ? "text-orange-300" : "text-rose-300"}`}>{c.statut}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="flex flex-wrap gap-2 border-b border-slate-800 pb-2">
        {TAB_LABELS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
              tab === key
                ? "bg-emerald-600 text-white"
                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-2 rounded-2xl border border-slate-800 bg-slate-900/40 p-3 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <Search className="h-4 w-4" />
          Recherche
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Code ATL001, produit, famille, mouvement, état…"
          className="min-w-0 flex-1 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white outline-none focus:border-emerald-500"
        />
        {search.trim() ? (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="rounded-lg border border-slate-700 px-3 py-2 text-xs text-slate-300 hover:bg-slate-800"
          >
            Effacer
          </button>
        ) : null}
      </div>

      {loading ? (
        <p className="flex items-center justify-center gap-2 rounded-2xl border border-slate-800 bg-slate-900/40 py-16 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" /> Chargement…
        </p>
      ) : tab === "stockCatalogue" ? (
        <AdminDataTableShell>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-950/80 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Produit</th>
                  <th className="px-4 py-3">Catégorie</th>
                  <th className="px-4 py-3">Famille</th>
                  <th className="px-4 py-3">Stock actuel</th>
                  <th className="px-4 py-3">Affecté</th>
                  <th className="px-4 py-3">Disponible réel</th>
                  <th className="px-4 py-3">Maintenance</th>
                  <th className="px-4 py-3">Pertes</th>
                  <th className="px-4 py-3">Seuil</th>
                  <th className="px-4 py-3">État stock</th>
                  <th className="px-4 py-3">Dernier mouvement</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCatalogDashboard.length === 0 ? (
                  <tr>
                    <td className="px-4 py-6 text-slate-400" colSpan={12}>
                      Aucune donnée catalogue. Créez des articles dans le catalogue puis enregistrez des entrées
                      stock.
                    </td>
                  </tr>
                ) : (
                  filteredCatalogDashboard.map((r) => (
                    <tr key={r.resourceCatalogId} className="border-b border-slate-800/80">
                      <td className="px-4 py-3 font-mono text-emerald-200">{r.code}</td>
                      <td className="px-4 py-3 text-slate-200">{r.displayName ?? r.nom}</td>
                      <td className="px-4 py-3 text-slate-400">{r.category}</td>
                      <td className="px-4 py-3 text-slate-400">{r.family ?? r.famille ?? "—"}</td>
                      <td className="px-4 py-3 text-slate-200">{r.stockActuel}</td>
                      <td className="px-4 py-3 text-sky-300">{r.reserve}</td>
                      <td className="px-4 py-3 text-emerald-300">{r.disponible ?? Math.max(0, r.stockActuel - r.reserve)}</td>
                      <td className="px-4 py-3 text-violet-300">{r.maintenance ?? 0}</td>
                      <td className="px-4 py-3 text-rose-300">{r.perte ?? 0}</td>
                      <td className="px-4 py-3 text-slate-400">{r.seuilAlerte ?? "—"}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                            r.stockActuel === 0
                              ? "bg-rose-500/15 text-rose-200"
                              : r.alertes.includes("SOUS_SEUIL")
                                ? "bg-amber-500/15 text-amber-200"
                                : "bg-emerald-500/15 text-emerald-200"
                          }`}
                        >
                          {r.stockActuel === 0 ? "Rupture critique" : r.alertes.includes("SOUS_SEUIL") ? "Stock faible" : "OK"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500">
                        {r.dernierMouvementAt
                          ? `${new Date(r.dernierMouvementAt).toLocaleString()} (${r.dernierMouvementKind ?? "?"})`
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-wrap justify-center gap-1">
                          <button
                            type="button"
                            className="rounded-lg border border-emerald-700 px-2 py-1 text-[11px] text-emerald-200"
                            onClick={() => {
                              setStockEntryCatalogId(r.resourceCatalogId);
                              setStockEntryOpen(true);
                            }}
                          >
                            Entrée stock
                          </button>
                          <button type="button" className="rounded-lg border border-slate-600 px-2 py-1 text-[11px]" onClick={() => void doCatalogStockAction(r, "SORTIE")}>Sortie manuelle</button>
                          <button type="button" className="rounded-lg border border-slate-600 px-2 py-1 text-[11px]" onClick={() => void doCatalogStockAction(r, "AJUSTEMENT")}>Ajuster</button>
                          <Link to="/admin/ressources/mouvements" className="rounded-lg border border-slate-600 px-2 py-1 text-[11px]">Historique</Link>
                          <Link to="/admin/ressources/catalogue" className="rounded-lg border border-slate-600 px-2 py-1 text-[11px]">Désactiver</Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </AdminDataTableShell>
      ) : tab === "mouvementsCatalogue" ? (
        <AdminDataTableShell>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-950/80 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Produit</th>
                  <th className="px-4 py-3">Qté</th>
                  <th className="px-4 py-3">Fournisseur</th>
                  <th className="px-4 py-3">Commentaire</th>
                </tr>
              </thead>
              <tbody>
                {filteredCatalogMovements.length === 0 ? (
                  <tr>
                    <td className="px-4 py-6 text-slate-400" colSpan={6}>
                      Aucun mouvement catalogue.
                    </td>
                  </tr>
                ) : (
                  filteredCatalogMovements.map((m) => (
                    <tr key={m.id} className="border-b border-slate-800/80">
                      <td className="px-4 py-3 text-slate-300 whitespace-nowrap">
                        {new Date(m.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sky-300">{m.kind}</td>
                      <td className="px-4 py-3 text-slate-200">
                        <span className="font-mono text-emerald-200/90">{m.resourceCatalog.code}</span>
                        <span className="block text-xs text-slate-500">{m.resourceCatalog.nom}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-300">{m.quantity}</td>
                      <td className="px-4 py-3 text-slate-400">{m.fournisseur ?? "—"}</td>
                      <td className="px-4 py-3 text-slate-500">{m.commentaire ?? "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </AdminDataTableShell>
      ) : tab === "mouvements" ? (
        <AdminDataTableShell>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-950/80 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Détail</th>
                  <th className="px-4 py-3">Ressource</th>
                  <th className="px-4 py-3">Quantité</th>
                  <th className="px-4 py-3">Client</th>
                  <th className="px-4 py-3">Agent</th>
                  <th className="px-4 py-3">Commentaire</th>
                </tr>
              </thead>
              <tbody>
                {movements.length === 0 ? (
                  <tr><td className="px-4 py-6 text-slate-400" colSpan={8}>Aucun mouvement.</td></tr>
                ) : (
                  movements.map((m) => {
                    const u = m.user as { prenom?: string; nom?: string } | undefined;
                    const agentLabel = u ? [u.prenom, u.nom].filter(Boolean).join(" ").trim() || "—" : "—";
                    const detail =
                      typeof m.displayLabel === "string" && m.displayLabel.trim()
                        ? m.displayLabel
                        : "—";
                    const clientCell =
                      typeof m.clientLabel === "string" && m.clientLabel.trim()
                        ? m.clientLabel
                        : m.destinationType === "client" && m.destinationId != null
                          ? `#${String(m.destinationId)}`
                          : m.clientId != null
                            ? `#${String(m.clientId)}`
                            : "—";
                    return (
                    <tr key={String(m.id)} className="border-b border-slate-800/80">
                      <td className="px-4 py-3 text-slate-300">{String(m.createdAt ?? "—")}</td>
                      <td className="px-4 py-3 text-slate-300">{String(m.movementType ?? "—")}</td>
                      <td className="max-w-xs px-4 py-3 text-slate-200">{detail}</td>
                      <td className="px-4 py-3 text-slate-300">#{String(m.ressourceId ?? "—")}</td>
                      <td className="px-4 py-3 text-slate-300">{String(m.quantite ?? "—")}</td>
                      <td className="px-4 py-3 text-slate-400">{clientCell}</td>
                      <td className="px-4 py-3 text-slate-400">{agentLabel}</td>
                      <td className="px-4 py-3 text-slate-400">{String(m.commentaire ?? "—")}</td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </AdminDataTableShell>
      ) : tab === "affectations" ? (
        <AdminDataTableShell>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-950/80 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Client/Equipe</th><th className="px-4 py-3">Ressource</th><th className="px-4 py-3">Quantite</th><th className="px-4 py-3">Plan associe</th><th className="px-4 py-3">Zone</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Statut</th>
                </tr>
              </thead>
              <tbody>
                {affectations.map((a) => (
                  <tr key={a.id} className="border-b border-slate-800/80">
                    <td className="px-4 py-3 text-slate-300">{a.client ?? a.equipe ?? "—"}</td>
                    <td className="px-4 py-3 text-slate-300">{a.ressource}</td>
                    <td className="px-4 py-3 text-slate-300">{a.quantite}</td>
                    <td className="px-4 py-3 text-slate-400">{a.planAssocie ?? "—"}</td>
                    <td className="px-4 py-3 text-slate-400">{a.zone ?? "—"}</td>
                    <td className="px-4 py-3 text-slate-400">{a.dateAffectation}</td>
                    <td className="px-4 py-3 text-slate-300">{a.statut}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AdminDataTableShell>
      ) : (
        <AdminDataTableShell>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-950/80 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Référence</th>
                  <th className="px-4 py-3">Ressource</th>
                  <th className="px-4 py-3">Famille</th>
                  <th className="px-4 py-3">Stock actuel</th>
                  <th className="px-4 py-3">Affecté</th>
                  <th className="px-4 py-3">Disponible réel</th>
                  <th className="px-4 py-3">État physique</th>
                  <th className="px-4 py-3">Maintenance</th>
                  <th className="px-4 py-3">Dernier mouvement</th>
                  <th className="px-4 py-3">Alertes</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/90">
                {filtered.map((r) => (
                  <tr key={r.id} className="bg-slate-900/20 hover:bg-slate-800/40">
                    <td className="px-4 py-3 text-slate-300">{r.reference ?? "—"}</td>
                    <td className="px-4 py-3 font-semibold text-emerald-200/95">{r.nom}<span className="block text-[11px] text-slate-500">{r.reference ?? "ref auto"}</span></td>
                    <td className="px-4 py-3 text-slate-300">
                      {r.famille.replace(/_/g, " ")}
                    </td>
                    <td className="px-4 py-3 text-slate-400">{r.stockActuel}</td>
                    <td className="px-4 py-3 text-sky-300">{r.stockAffecte}</td>
                    <td className="px-4 py-3 text-emerald-300">{r.stockDisponible}</td>
                    <td className="px-4 py-3 text-slate-400">{r.etat}</td>
                    <td className="px-4 py-3 text-violet-300">{r.maintenanceStatus}</td>
                    <td className="px-4 py-3 text-slate-500">{r.dernierMouvementAt ?? "—"}</td>
                    <td className="px-4 py-3 text-xs text-orange-300">{r.alertes.join(", ") || "—"}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-wrap justify-center gap-1">
                        <button type="button" className="rounded-lg border border-slate-600 px-2 py-1 text-[11px]" onClick={() => void doStockAction(r, "ENTREE")}>Entrée</button>
                        <button type="button" className="rounded-lg border border-slate-600 px-2 py-1 text-[11px]" onClick={() => void doStockAction(r, "SORTIE")}>Sortie</button>
                        <button type="button" className="rounded-lg border border-slate-600 px-2 py-1 text-[11px]" onClick={() => { setSelected(r); setAffectOpen(true); }}>Affecter</button>
                        <button type="button" className="rounded-lg border border-slate-600 px-2 py-1 text-[11px]" onClick={() => void doStockAction(r, "RETOUR")}>Retour</button>
                        <button type="button" className="rounded-lg border border-slate-600 px-2 py-1 text-[11px]" onClick={() => void doStockAction(r, "AJUSTEMENT")}>Ajuster</button>
                        <button type="button" className="rounded-lg border border-slate-600 px-2 py-1 text-[11px]">Maintenance</button>
                        <button type="button" className="rounded-lg border border-slate-600 px-2 py-1 text-[11px]">Historique</button>
                        <button type="button" title="Retirer du parc actif" className="rounded-lg border border-slate-600 bg-slate-800/80 p-2 text-rose-400 hover:border-rose-500/40" onClick={() => void handleRetirer(r)}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AdminDataTableShell>
      )}

      <ResourceStockEntryModal
        open={stockEntryOpen}
        initialCatalogId={stockEntryCatalogId}
        onClose={() => {
          setStockEntryOpen(false);
          setStockEntryCatalogId(null);
        }}
        onSuccess={() => {
          void load();
          if (tab === "stockCatalogue" || tab === "mouvementsCatalogue") void loadCatalogTabs();
        }}
      />
      <AffecterRessourceModal
        open={affectOpen}
        ressource={selected}
        onClose={() => {
          setAffectOpen(false);
          setSelected(null);
        }}
        onSuccess={() => void load()}
      />
    </div>
  );
};

export default RessourcesOperationnellesPage;
